import { lazy } from 'react';
import { UserRole } from '../hooks/useUser';

// Lazy load components for code splitting
const ClientReportView = lazy(() =>
	import('@/views/ClientReportView').then(m => ({ default: m.ClientReportView })),
);
const ClientsListView = lazy(() =>
	import('@/views/Clients/ClientsListView').then(m => ({ default: m.ClientsListView })),
);
const ClientView = lazy(() =>
	import('@/views/Clients/ClientView').then(m => ({ default: m.ClientView })),
);
const DemoView = lazy(() => import('@/views/DemoView').then(m => ({ default: m.DemoView })));
const PlaygroundView = lazy(() =>
	import('@/views/Playground').then(m => ({ default: m.PlaygroundView })),
);
const TranscriptsView = lazy(() =>
	import('@/views/tools/TranscriptsView').then(m => ({ default: m.TranscriptsView })),
);
const WelcomeView = lazy(() =>
	import('@/views/WelcomeView').then(m => ({ default: m.WelcomeView })),
);

export interface RouteConfig {
	path: string;
	element: React.ComponentType;
	allowedRoles: UserRole[];
	navigationHidden?: boolean;
	label: string;
}

export const routeConfigs: RouteConfig[] = [
	{
		path: '/',
		element: WelcomeView,
		allowedRoles: ['client', 'staff', 'admin'],
		label: 'Dashboard',
	},
	{
		path: '/clients',
		element: ClientsListView,
		allowedRoles: ['staff', 'admin'],
		label: 'Clients',
	},
	{
		path: '/clients/:clientId',
		element: ClientView,
		allowedRoles: ['staff', 'admin'],
		label: 'Client Details',
		navigationHidden: true,
	},
	{
		path: '/report',
		element: ClientReportView,
		allowedRoles: ['admin'],
		label: 'Reports',
	},
	{
		path: '/transcripts',
		element: TranscriptsView,
		allowedRoles: ['staff', 'admin'],
		label: 'Transcripts',
	},
	{
		path: '/demo',
		element: DemoView,
		allowedRoles: ['staff', 'admin'],
		label: 'Demo',
	},
	{
		path: '/playground',
		element: PlaygroundView,
		allowedRoles: ['admin'],
		label: 'Playground',
	},
];

/**
 * Filter routes based on user role
 */
export const getRoutesForRole = (role: UserRole): RouteConfig[] => {
	return routeConfigs.filter(route => route.allowedRoles.includes(role));
};

/**
 * Check if a user has access to a specific route
 */
export const hasRouteAccess = (path: string, role: UserRole): boolean => {
	const route = routeConfigs.find(r => r.path === path);
	return route ? route.allowedRoles.includes(role) : false;
};

/**
 * Get navigation items for a specific role
 */
export const getNavigationForRole = (role: UserRole) => {
	return getRoutesForRole(role)
		.filter(route => !route.navigationHidden)
		.map(route => ({
			path: route.path,
			label: route.label || route.path,
		}));
};
